import React, {useState, useCallback, useMemo} from 'react';
import {ThemeProvider, Box} from '@mui/material';
import NavBar from '../../pieces/navbar/navbar';
import {NewPermissionGate} from '../../permissions/permissionGate';
import myTheme from '../../styles/myStyles';
import {contractorTagJobRules, CONTRACTOR_TAG_ELEMENTS} from '../../permissions/contractorTagJobAccessControl';
import {companyAndUserUtils} from '../../utils/companyAndUserUtils';
import DisplayListsPageWrapper from '../wrappers/displayListsPageWrapper';
import ListPageHeader from '../../pieces/listPageHeader';
import {ListViewHeader} from '../jobs/components/listViewHeader';
import usePagination from '../../hooks/usePagination';
import {useGridViewState} from '../../hooks/useGridViewState';
import {globalUseClasses} from '../../styles/globalClasses';
import {TAG_ELEMENTS, tagsPageRules} from '../../permissions/tagAccessControl';
import AllTagJobsGrid from './allTagJobsGrid';
import TagJobForm from './tagJobForm';
import TagModal from '../../pieces/modals/tagModal';
import ConfirmDelete from '../../pieces/modals/confirmDelete';
import base_url from '../../baseUrls';
import {useSelector} from 'react-redux';
import {useGetFilterList} from '../../hooks/useGetFilterList';
import Loader from '../misc/loader';
import * as STYLE_CONSTANTS from '../../styles/styleConstants';

const AllTagJobsView = () => {
  const viewController = useGridViewState();
  const classes = globalUseClasses();
  const pagination = usePagination();
  const company = useSelector(state => state.company);

  /**
   * Companies this user may read/manage tags for. Fetched here rather than in the grid and
   * the form separately: they both need it, it decides the grid's columns, and owning it
   * here lets the page hold back everything below the header until it lands.
   */
  const {options: companyScope, isLoading: companyScopeLoading} =
    useGetFilterList({url: `${base_url.api}tagJobs/companies`});
  const tagCompanies = useMemo(
    () => (companyScope?.companies ?? []).filter((c) => c.companyId && c.companyName),
    [companyScope]
  );

  const [showAddTagPopup, setShowAddTagPopup] = useState(false);
  const [showConfirmDeletePopup, setShowConfirmDeletePopup] = useState(false);
  const handleConfirmDeletePopupClose = () => setShowConfirmDeletePopup(false);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleAddTagOpen = useCallback(() => setShowAddTagPopup(true), []);
  const handleAddTagClose = () => setShowAddTagPopup(false);

  const [showEditTagPopup, setShowEditTagPopup] = useState(false);
  const handleEditTagClose = () => setShowEditTagPopup(false);


  const [selectedTag, setSelectedTag] = useState(undefined);


  const editTagHandler = (data)=> {
    setSelectedTag(data);
    setShowEditTagPopup(true);
  };

  const editTagCancel = ()=> {
    setSelectedTag(undefined);
    setShowEditTagPopup(false);
  };

  const handleSuccess = ()=> {
    if(showAddTagPopup || showEditTagPopup || showConfirmDeletePopup) {
      window.location.reload();
    }
  };

  const deleteTagHandler = (data)=> {
    setSelectedTag(data);
    setShowConfirmDeletePopup(true);
  };

  const getDeleteURL = ()=> {
    if(!selectedTag) return null;
    return `${base_url.api}tagJobs/delete`;
  };

  const deleteCancelHandler = ()=> {
   
    setSelectedTag(undefined);
    setShowConfirmDeletePopup(false);
  };

  const getHeaderComponent = ()=> {
    let title = 'All Contractor Tags';

    if(pagination.totalItemsCount) {
      title = `${title} (${pagination.totalItemsCount})`;
    }
    return (
      <ListViewHeader title={title}
        //gridEnum={JobGridEnum}
        pagination={pagination}
        //handleClick={handleClick}
        // menuController={menuController}
        // extendedFilters={shouldHideFilters() ? null : extendedFilters}
        viewController={viewController}
      />
    );
  };
  const headerComponent = <ListPageHeader header={'All Contractor Tags'}
    viewController={viewController}
    headComponent = {getHeaderComponent()}
    addHandler={handleAddTagOpen}
    totalItems={pagination.totalItemsCount}
    showAddButton = {NewPermissionGate({
          user: viewController.user,
          elementPermissions: contractorTagJobRules[CONTRACTOR_TAG_ELEMENTS.addTagButton]
        }) && companyAndUserUtils.canAddNewEntityToCompany(company)}
    // showAddButton = {NewPermissionGate({
    //   user: viewController.user,
    //   elementPermissions: tagsPageRules[TAG_ELEMENTS.addTagButton],
    // })}
  >
    {pagination.ItemsController}
  </ListPageHeader>;

  return (
    viewController.RenderElement(
      <>
        <NavBar/>
        <ThemeProvider theme={myTheme}>
          <Box className={classes.page}>
            <TagModal open={showEditTagPopup}
              handleClose={handleEditTagClose}
              header={'Edit tag'}
              preventScroll={false}
            >
              <TagJobForm closeModal={handleEditTagClose}
                editMode={true}
                successAction={handleSuccess}
                tagCompanies={tagCompanies}
                tagData={selectedTag}/>
            </TagModal>

            <TagModal open={showAddTagPopup}
              handleClose={handleAddTagClose}
              header={'Create tag'}
              preventScroll={false}
            >
              <TagJobForm closeModal={handleAddTagClose}
                successAction={handleSuccess}
                tagCompanies={tagCompanies}
                editMode={false}/>
            </TagModal >

            <ConfirmDelete deleteUrl={getDeleteURL()}
              open={showConfirmDeletePopup}
              submitDeleteAction={handleConfirmDeletePopupClose}
              handleCancel={deleteCancelHandler}
              successAction={handleSuccess}
              idToDelete={selectedTag?.id}
              entityName={'tagJob'}
              recordName={selectedTag ? selectedTag.tagName : ''}
            />

            <DisplayListsPageWrapper
              headerComponent={headerComponent}>
              {companyScopeLoading ?
                /* Covers the grid and the paginator, i.e. everything below the page header.
                   Height mirrors AlpineContainer's, plus the paginator row it replaces. */
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: `calc(100vh - ${STYLE_CONSTANTS.HEIGHT.NAVBAR({isXS: false})} - 50px - 20px)`,
                  backgroundColor: STYLE_CONSTANTS.COLORS.white,
                }}>
                  <Loader useOwnWrapper timeout={50}/>
                </Box> :
                <>
                  <AllTagJobsGrid
                    editTagHandler={editTagHandler}
                    editTagCancel={editTagCancel}
                    viewController={viewController}
                    deleteTagHandler={deleteTagHandler}
                    deleteTagCancel={deleteCancelHandler}
                    tagCompanies={tagCompanies}

                    // setBlocked={viewController.setBlocked}
                    pagination={pagination}
                  />
                  {pagination.PageController}
                </>
              }
            </DisplayListsPageWrapper>
          </Box>
        </ThemeProvider>
      </>
    )
  );
};
export default AllTagJobsView;
